/**
 * useAdminUsers — управление пользователями (только admin).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/api/client';

export interface AdminUser {
  id: number;
  username: string;
  display_name: string;
  role: 'user' | 'admin' | 'seo';
  last_login_at: string | null;
  /** Заполнен после первого входа через SSO — учётка управляется в КОММА IDM. */
  keycloak_sub: string | null;
  /** Логин в Keycloak; проставляется заранее, чтобы связать существующую учётку. */
  keycloak_username: string | null;
}

export function useAdminUsers() {
  return useQuery<AdminUser[]>({
    queryKey: ['admin', 'users'],
    queryFn: () => apiFetch<AdminUser[]>('/api/admin/users'),
  });
}

export function useAdminUserMutations() {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ['admin', 'users'] });

  const create = useMutation({
    mutationFn: (u: { username: string; password: string; display_name: string; role: 'user' | 'admin' | 'seo' }) =>
      apiFetch('/api/admin/users', { method: 'POST', json: u }),
    onSuccess: invalidate,
  });
  const patch = useMutation({
    mutationFn: ({ id, ...body }: { id: number; role?: 'user' | 'admin' | 'seo'; password?: string }) =>
      apiFetch(`/api/admin/users/${id}`, { method: 'PATCH', json: body }),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: (id: number) => apiFetch(`/api/admin/users/${id}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });
  return { create, patch, remove };
}
